/**
 * WatchpointMultiselect — compact toggle list of project monitors.
 * Used by ActionRow for the check_linked_services action type.
 * Toggles watchpoint IDs in/out of the config.watchpoint_ids array.
 */
import { Badge } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Check } from 'lucide-react';
import type { Watchpoint } from '@/api/healthcheck';

interface Props {
  label: string;
  selected: string[];
  watchpoints: Watchpoint[];
  required?: boolean;
  showError?: boolean;
  onChange: (ids: string[]) => void;
}

export function WatchpointMultiselect({
  label,
  selected,
  watchpoints,
  required,
  showError,
  onChange,
}: Props) {
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const isEmpty = showError && required && selected.length === 0;

  return (
    <Field
      label={label}
      required={required}
      error={isEmpty ? 'Select at least one monitor.' : undefined}
    >
      {() => (
        <div
          style={{
            border: `1px solid ${isEmpty ? 'var(--vn-danger-border)' : 'var(--vn-border-default)'}`,
            borderRadius: 'var(--vn-radius-md)',
            maxHeight: 200,
            overflowY: 'auto',
            padding: 'var(--vn-space-1)',
          }}
        >
          {watchpoints.length === 0 ? (
            <p
              style={{
                margin: 'var(--vn-space-2)',
                color: 'var(--vn-fg-muted)',
                fontSize: 'var(--vn-font-size-sm)',
              }}
            >
              No monitors found for this project.
            </p>
          ) : (
            watchpoints.map((wp) => {
              const isSelected = selected.includes(wp.id);
              return (
                <button
                  key={wp.id}
                  type="button"
                  onClick={() => toggle(wp.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    width: '100%',
                    padding: 'var(--vn-space-2) var(--vn-space-2)',
                    background: isSelected ? 'var(--vn-accent-subtle)' : 'transparent',
                    border: 'none',
                    borderRadius: 'var(--vn-radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: 'var(--vn-font-size-sm)',
                    color: 'var(--vn-fg-default)',
                  }}
                  aria-pressed={isSelected}
                >
                  {/* Checkmark indicator */}
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 'var(--vn-radius-sm)',
                      border: `1px solid ${isSelected ? 'var(--vn-accent-solid)' : 'var(--vn-border-strong)'}`,
                      background: isSelected ? 'var(--vn-accent-solid)' : 'transparent',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--vn-fg-on-solid)',
                      fontSize: 10,
                    }}
                  >
                    {isSelected && <Check size={10} strokeWidth={3} />}
                  </span>
                  <span style={{ flex: 1, fontWeight: isSelected ? 500 : 400 }}>{wp.name}</span>
                  <Badge tone="neutral" appearance="subtle" size="sm">
                    {wp.type}
                  </Badge>
                </button>
              );
            })
          )}
        </div>
      )}
    </Field>
  );
}
